/**
 * Utilidades para la interfaz de usuario
 * @module utils/Ui
 */

import Plyr from 'plyr/dist/plyr.js';
import 'plyr/dist/plyr.css';

let overlayTimeoutId = null;
let releaseTimeoutId = null;

const DEFAULT_OVERLAY_ICON = 'rocket_launch';

function cancelScheduledRelease() {
    if (releaseTimeoutId) {
        clearTimeout(releaseTimeoutId);
        releaseTimeoutId = null;
    }
}

/**
 * Bloquea o desbloquea la interacción táctil de la página.
 * @param {boolean} block
 * @param {boolean} showOverlay
 * @param {{
 *   overlayIcon?: string,
 *   overlayText?: string,
 *   releaseAfterMs?: number,
 * }} [opts]
 * Opcional **`releaseAfterMs`**: tras ese tiempo se llama a `pageTouch(false)` desde ahora; si omitís, liberás con `pageTouch(false)`.
 */
export function pageTouch(block, showOverlay = false, opts = {}) {
    const { overlayIcon = DEFAULT_OVERLAY_ICON, overlayText, releaseAfterMs } = opts;

    if (block) {
        cancelScheduledRelease();

        document.body.classList.add('app-block-touch');

        if (typeof releaseAfterMs === 'number' && releaseAfterMs > 0) {
            releaseTimeoutId = setTimeout(() => {
                releaseTimeoutId = null;
                pageTouch(false);
            }, releaseAfterMs);
        }

        if (showOverlay) {
            if (overlayTimeoutId) {
                clearTimeout(overlayTimeoutId);
            }

            overlayTimeoutId = setTimeout(() => {
                let overlay = document.getElementById('app-loading-overlay');
                if (!overlay) {
                    overlay = document.createElement('div');
                    overlay.id = 'app-loading-overlay';
                    overlay.className = 'app-block-overlay';
                    document.body.appendChild(overlay);
                }

                overlay.style.display = 'flex';
                overlay.replaceChildren();

                const text =
                    typeof overlayText === 'string' && overlayText.trim() ? overlayText.trim() : '';
                if (text) { 
                    const heading = document.createElement('h1');
                    heading.className = 'title h1 text-yellow text-center mb-0';
                    heading.innerHTML = text;
                    overlay.appendChild(heading);
                } else {
                    const icon = document.createElement('i');
                    icon.className =
                        'material-symbols-outlined animate__animated animate__pulse animate__infinite';
                    icon.textContent = overlayIcon;
                    overlay.appendChild(icon);
                }

                overlayTimeoutId = null;
            }, 10);
        }
    } else {
        cancelScheduledRelease();

        if (overlayTimeoutId) {
            clearTimeout(overlayTimeoutId);
            overlayTimeoutId = null;
        }

        document.body.classList.remove('app-block-touch');

        const overlay = document.getElementById('app-loading-overlay');
        if (overlay) overlay.remove();
    }
}

/**
 * Configura la imagen de ejemplo en la página
 * @param {HTMLElement} page - Elemento de la página
 * @param {string} examplePath - Ruta de la imagen de ejemplo
 * @param {string} selector - Selector del contenedor (default: '.upload-example')
 */
export function setupExampleImage(page, examplePath, selector = '.upload-example') {
    const photoWrap = page.querySelector(selector);
    if (!photoWrap) return;

    if (examplePath && window.App?.settings?.contentUrl) {
        photoWrap.innerHTML = `<img src='${window.App.settings.contentUrl}/others/actions/${examplePath}' class='img-fluid rounded'>`;
    } else {
        photoWrap.remove();
    }
}

/**
 * Configura un reproductor de video Plyr
 * @param {HTMLElement} page - Elemento de la página
 * @param {string} videoId - ID del video de YouTube
 * @param {string} playerSelector - Selector del elemento del player (default: '#video-player')
 * @param {string} containerSelector - Selector del contenedor (default: '.upload-player')
 * @returns {Plyr|null} Instancia de Plyr o null
 */
export function setupVideoPlayer(page, videoId, playerSelector = '#video-player', containerSelector = '.upload-player') {
    if (!videoId) {
        const container = page.querySelector(containerSelector);
        if (container) container.remove();
        return null;
    }

    const container = page.querySelector(containerSelector);
    const videoElement = page.querySelector(playerSelector);

    if (!container || !videoElement) return null;

    videoElement.setAttribute('data-plyr-embed-id', videoId);

    const player = new Plyr(videoElement, {
        autoplay: false,
        controls: ['play-large', 'play', 'fullscreen', 'progress', 'pip', 'airplay', 'mute', 'volume'],
        youtube: { noCookie: true }
    });

    const navElement = page.querySelector('.app-page-nav');
    if (navElement) {
        player.on('enterfullscreen', () => navElement.classList.add('d-none'));
        player.on('exitfullscreen', () => navElement.classList.remove('d-none'));
    }

    return player;
}

export {
    celebratePreset,
    celebrateGrand,
    celebrateConfettiRain,
    celebrateConfettiBurst,
    celebrateConfettiFireworks,
    celebrateSparksBurst,
} from './Celebrations.js';
